import { normalizeHeader } from '../domain/businessRules'

interface ZipEntry {
  async(type: 'string'): Promise<string>
}

interface ZipArchive {
  file(path: string): ZipEntry | null
}

interface JSZipRuntime {
  loadAsync(data: ArrayBuffer): Promise<ZipArchive>
}

function runtime(): JSZipRuntime {
  const value = (globalThis as typeof globalThis & { JSZip?: JSZipRuntime }).JSZip
  if (!value) throw new Error('O leitor XLSX local não foi carregado.')
  return value
}

function xmlDecode(value: unknown): string {
  const named: Record<string, string> = {
    amp: '&',
    lt: '<',
    gt: '>',
    quot: '"',
    apos: "'",
  }
  return String(value ?? '').replace(
    /&(#x?[0-9a-fA-F]+|amp|lt|gt|quot|apos);/g,
    (match, key: string) => {
      if (named[key]) return named[key]
      if (key[0] === '#') {
        const hex = key[1]?.toLowerCase() === 'x'
        const number = Number.parseInt(key.slice(hex ? 2 : 1), hex ? 16 : 10)
        return Number.isFinite(number) ? String.fromCodePoint(number) : match
      }
      return match
    },
  )
}

function attribute(xml: string, name: string): string | null {
  const expression = new RegExp(
    `(?:^|\\s)${name.replace(/:/g, '\\:')}\\s*=\\s*["']([^"']*)["']`,
    'i',
  )
  const match = xml.match(expression)
  return match ? xmlDecode(match[1]) : null
}

function columnIndex(reference: string | null): number {
  const match = String(reference ?? '').match(/^([A-Z]+)/i)
  if (!match) return -1
  let value = 0
  for (const character of match[1].toUpperCase()) {
    value = value * 26 + character.charCodeAt(0) - 64
  }
  return value - 1
}

function normalizePath(path: string): string {
  const parts: string[] = []
  for (const part of path.split('/')) {
    if (!part || part === '.') continue
    if (part === '..') parts.pop()
    else parts.push(part)
  }
  return parts.join('/')
}

function resolvePath(base: string, target: string): string {
  if (target.startsWith('/')) return target.slice(1)
  return normalizePath(`${base}/${target}`)
}

export function parseSharedStrings(xml: string): string[] {
  const values: string[] = []
  const itemExpression = /<si\b[^>]*>([\s\S]*?)<\/si>/gi
  let item: RegExpExecArray | null
  while ((item = itemExpression.exec(xml))) {
    let text = ''
    const textExpression = /<t\b[^>]*>([\s\S]*?)<\/t>/gi
    let part: RegExpExecArray | null
    while ((part = textExpression.exec(item[1]))) text += xmlDecode(part[1])
    values.push(text)
  }
  return values
}

export function parseSheetRows(
  xml: string,
  shared: string[],
  maxRows = Number.POSITIVE_INFINITY,
): unknown[][] {
  const rows: unknown[][] = []
  const rowExpression = /<row\b([^>]*)>([\s\S]*?)<\/row>/gi
  let rowMatch: RegExpExecArray | null

  while ((rowMatch = rowExpression.exec(xml)) && rows.length < maxRows) {
    const row: unknown[] = []
    const cellExpression = /<c\b([^>]*?)(?:\/\s*>|>([\s\S]*?)<\/c>)/gi
    let cellMatch: RegExpExecArray | null

    while ((cellMatch = cellExpression.exec(rowMatch[2]))) {
      const attributes = cellMatch[1] ?? ''
      const inner = cellMatch[2] ?? ''
      const index = columnIndex(attribute(attributes, 'r'))
      if (index < 0) continue

      const type = attribute(attributes, 't')
      const value: unknown = (() => {
        if (type === 'inlineStr') {
          let text = ''
          const textExpression = /<t\b[^>]*>([\s\S]*?)<\/t>/gi
          let part: RegExpExecArray | null
          while ((part = textExpression.exec(inner))) text += xmlDecode(part[1])
          return text
        }

        const valueMatch = inner.match(/<v\b[^>]*>([\s\S]*?)<\/v>/i)
        const raw = valueMatch ? xmlDecode(valueMatch[1]) : ''
        if (type === 's') return shared[Number(raw)] ?? ''
        if (type === 'str' || type === 'e') return raw
        if (type === 'b') return raw === '1'
        if (raw !== '' && Number.isFinite(Number(raw))) return Number(raw)
        return raw
      })()

      row[index] = value
    }
    rows.push(row)
  }

  return rows
}

interface WorkbookSheet {
  name: string
  relationshipId: string
}

function parseWorkbookSheets(xml: string): WorkbookSheet[] {
  const sheets: WorkbookSheet[] = []
  const expression = /<sheet\b([^>]*?)(?:\/\s*>|>)/gi
  let match: RegExpExecArray | null
  while ((match = expression.exec(xml))) {
    const name = attribute(match[1], 'name')
    const relationshipId = attribute(match[1], 'r:id')
    if (name && relationshipId) sheets.push({ name, relationshipId })
  }
  return sheets
}

function parseRelationships(xml: string): Map<string, string> {
  const relationships = new Map<string, string>()
  const expression = /<Relationship\b([^>]*?)(?:\/\s*>|>)/gi
  let match: RegExpExecArray | null
  while ((match = expression.exec(xml))) {
    const id = attribute(match[1], 'Id')
    const target = attribute(match[1], 'Target')
    if (id && target) relationships.set(id, target)
  }
  return relationships
}

export function chooseWorkbookSheet(sheets: WorkbookSheet[]): WorkbookSheet | null {
  return sheets.find(sheet => normalizeHeader(sheet.name) === 'base') ?? sheets[0] ?? null
}

export interface ParsedXlsx {
  rows: unknown[][]
  sheetName: string
}

export async function parseXlsxBuffer(buffer: ArrayBuffer): Promise<ParsedXlsx> {
  const zip = await runtime().loadAsync(buffer)
  const workbookEntry = zip.file('xl/workbook.xml')
  const relationshipsEntry = zip.file('xl/_rels/workbook.xml.rels')
  if (!workbookEntry || !relationshipsEntry) {
    throw new Error('O arquivo não possui uma estrutura XLSX válida.')
  }

  const sharedEntry = zip.file('xl/sharedStrings.xml')
  const [workbookXml, relationshipsXml, sharedXml] = await Promise.all([
    workbookEntry.async('string'),
    relationshipsEntry.async('string'),
    sharedEntry ? sharedEntry.async('string') : Promise.resolve(''),
  ])

  const shared = sharedXml ? parseSharedStrings(sharedXml) : []
  const sheets = parseWorkbookSheets(workbookXml)
  if (!sheets.length) throw new Error('Nenhuma planilha foi encontrada no arquivo.')

  const selected = chooseWorkbookSheet(sheets)
  if (!selected) throw new Error('Nenhuma planilha válida foi encontrada no arquivo.')

  const relationships = parseRelationships(relationshipsXml)
  const target = relationships.get(selected.relationshipId)
  if (!target) throw new Error('Não foi possível localizar a planilha principal no XLSX.')

  const entry = zip.file(resolvePath('xl', target))
  if (!entry) throw new Error('A planilha indicada pelo arquivo não foi encontrada.')

  return {
    rows: parseSheetRows(await entry.async('string'), shared),
    sheetName: selected.name,
  }
}
